import {
  StarIcon,
  ArrowUpCircleIcon,
  ArrowPathIcon,
} from "@heroicons/react/16/solid";
import { stringUtility } from "@repo/utilities/string.utility.ts";
import { FieldError } from "react-hook-form";

interface IMultiFileInputProps {
  supportFormat: string[];
  maxSize: string;
  handleChange: (_file: FileList) => void;
  name: string;
  value: FileList;
  error?: FieldError;
  className?: string;
  label?: string;
  isLoading?: boolean;
  isRequired?: boolean;
  accessorKey?: string;
  ellipsis?: number;
  hideInfo?: boolean;
  placeholder?: string;
}

const MultiFileInputBase = ({
  supportFormat,
  maxSize,
  name,
  handleChange,
  value,
  error,
  className,
  label,
  isRequired,
  isLoading,
  accessorKey = "name",
  ellipsis = 50,
  hideInfo = false,
  placeholder = "",
}: IMultiFileInputProps) => {
  const handleDrop = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    e.stopPropagation();

    const selectedFile = e.dataTransfer.files;
    if (selectedFile) {
      handleChange(selectedFile);
    }
  };

  return (
    <section className={`w-full flex flex-col gap-1 ${className}`}>
      {label && (
        <p className="text-xs flex items-center gap-1 text-left text-body font-semibold pl-1">
          {label}
          {isRequired && (
            <StarIcon className="size-2 text-error -translate-y-1" />
          )}
        </p>
      )}
      <label
        htmlFor={"upload_file" + name}
        className={`relative cursor-pointer rounded w-full px-2 
        h-10 flex items-center justify-between  gap-2  ring-1 bg-white text-sm font-medium border border-transparent   focus-within:border focus-within:border-primary/50 focus-within:outline-none  ${error ? "ring-error/50" : "ring-extraLightGray "}`}
        onDragOver={(e) => e.preventDefault()}
        onDragEnter={(e) => e.preventDefault()}
        onDrop={handleDrop}
      >
        {value ? (
          <div className="flex  items-center">
            <p className="text-primary text-sm font-semibold">
              {stringUtility?.addEllipsisAtEnd(
                value?.[0]?.name || accessorKey,
                ellipsis,
              )}
              {value?.length - 1 ? (
                <span className="bg-primary/10 text-primary p-1 rounded mx-2">
                  +{value?.length - 1}
                </span>
              ) : (
                <></>
              )}
            </p>
          </div>
        ) : (
          <p className="text-sm font-medium text-darkGray/80">
            {placeholder || "Drag and drop your file here or click to upload"}
          </p>
        )}
        {!isLoading ? (
          <ArrowUpCircleIcon
            className={`size-8 p-1.5 rounded text-subHeading`}
          />
        ) : (
          <ArrowPathIcon className="size-5 text-subHeading animate-spin" />
        )}
      </label>
      <input
        name={name}
        onChange={(e) => {
          const selectedFile = e.target.files;
          if (selectedFile?.length) {
            handleChange(selectedFile);
          }
        }}
        multiple={true}
        id={"upload_file" + name}
        accept={supportFormat?.join(",")}
        type="file"
        className="hidden"
      />
      {error ? (
        <section className="text-xs text-error font-medium flex items-center justify-between">
          {error?.message}
        </section>
      ) : !hideInfo ? (
        <section className="text-xs text-darkGray font-medium flex items-center justify-between">
          <p>Accept:{supportFormat?.join(", ")}</p>
          <p>Max size: {maxSize}</p>
        </section>
      ) : null}
    </section>
  );
};

export default MultiFileInputBase;
